<template>
  <div class="ca-hist-detail" role="dialog" :aria-label="person.username || person.phone">
    <div class="ca-hist-detail__backdrop" @click="$emit('close')"></div>

    <aside class="ca-hist-detail__panel" :dir="isRTL ? 'rtl' : 'ltr'">
      <header class="ca-hist-detail__header">
        <div class="ca-hist-detail__identity">
          <h2 class="ca-hist-detail__name">{{ person.username || $t('ca_hist_unknown_name') }}</h2>
          <div class="ca-hist-detail__meta">
            <span v-if="person.phone">{{ person.phone }}</span>
            <span v-if="person.totals.isClockedInNow" class="ca-hist-detail__badge is-live">{{ $t('ca_hist_clocked_in_now') }}</span>
            <span v-if="!person.isAssigned" class="ca-hist-detail__badge">{{ $t('ca_hist_not_assigned') }}</span>
            <span v-else-if="!person.shiftIds.length" class="ca-hist-detail__badge">{{ $t('ca_hist_no_shift') }}</span>
          </div>
        </div>
        <div class="ca-hist-detail__nav">
          <button type="button" :disabled="!hasPrev" :title="$t('ca_hist_prev_person')" @click="$emit('prev')">
            <span aria-hidden="true">{{ isRTL ? '›' : '‹' }}</span>
          </button>
          <button type="button" :disabled="!hasNext" :title="$t('ca_hist_next_person')" @click="$emit('next')">
            <span aria-hidden="true">{{ isRTL ? '‹' : '›' }}</span>
          </button>
          <button type="button" :title="$t('ca_hist_close')" @click="$emit('close')">
            <span aria-hidden="true">✕</span>
          </button>
        </div>
      </header>

      <div ref="body" class="ca-hist-detail__body">
        <div class="ca-hist-detail__stats">
          <div v-for="stat in stats" :key="stat.key" class="ca-hist-detail__stat" :class="stat.tone && `is-${stat.tone}`">
            <span class="ca-hist-detail__stat-value">{{ stat.value }}</span>
            <span class="ca-hist-detail__stat-label">{{ $t(stat.label) }}</span>
          </div>
        </div>

        <div class="ca-hist-detail__calendars">
          <div v-for="month in months" :key="month.key" class="ca-hist-cal">
            <div class="ca-hist-cal__title">{{ monthLabel(month) }}</div>
            <div class="ca-hist-cal__grid">
              <span v-for="(name, index) in weekdayNames" :key="`h-${index}`" class="ca-hist-cal__weekday">{{ name }}</span>
              <template v-for="(week, weekIndex) in month.weeks">
                <button
                  v-for="(date, dayIndex) in week"
                  :key="`${month.key}-${weekIndex}-${dayIndex}`"
                  type="button"
                  class="ca-hist-cal__day"
                  :class="date ? `is-${dayState(date)}` : 'is-pad'"
                  :disabled="!date || !dayByDate[date]"
                  :title="date ? dayTitle(date) : ''"
                  @click="scrollToDay(date)"
                >{{ date ? Number(date.slice(8)) : '' }}</button>
              </template>
            </div>
          </div>
        </div>

        <div class="ca-hist-detail__legend">
          <span v-for="item in legend" :key="item" class="ca-hist-detail__legend-item">
            <span class="ca-hist-cal__swatch" :class="`is-${item}`"></span>
            {{ $t(`ca_hist_legend_${item.replace(/-/g, '_')}`) }}
          </span>
        </div>

        <p v-if="!dayRows.length" class="ca-hist-detail__empty">{{ $t('ca_hist_empty') }}</p>

        <ol class="ca-hist-days">
          <li
            v-for="row in dayRows"
            :id="`ca-hist-day-${row.date}`"
            :key="row.date"
            class="ca-hist-day"
            :class="{ 'is-missed': row.isMissed, 'is-flash': flashDate === row.date }"
          >
            <div class="ca-hist-day__head">
              <span class="ca-hist-day__date">{{ row.label }}</span>
              <span class="ca-hist-day__chips">
                <span v-if="row.late" class="ca-hist-day__chip is-late" :title="row.lateTitle">
                  {{ $t('ca_hist_late_by', { duration: row.lateLabel }) }}
                </span>
                <span v-if="row.isMissed" class="ca-hist-day__chip is-missed" :title="$t('ca_hist_missed_tip')">
                  {{ $t('ca_hist_missed') }}
                </span>
                <span v-if="row.hasMissingClockOut" class="ca-hist-day__chip is-warning" :title="$t('ca_hist_no_clock_out_tip')">
                  {{ $t('ca_hist_no_clock_out') }}
                </span>
                <span v-if="row.hasInProgress" class="ca-hist-day__chip is-live">{{ $t('ca_hist_in_progress') }}</span>
              </span>
              <span v-if="row.isWorked" class="ca-hist-day__net">{{ row.netLabel }}</span>
            </div>

            <div v-if="row.isWorked" class="ca-hist-day__body">
              <div class="ca-hist-day__punches">
                <span v-for="session in row.sessions" :key="session.key" class="ca-hist-day__punch" :class="`is-${session.status}`">
                  {{ session.inLabel }} → {{ session.outLabel }}
                  <sup v-if="session.nextDay">+1</sup>
                  <span v-if="session.isAuto" class="ca-hist-day__tag">{{ $t('ca_hist_auto') }}</span>
                </span>
              </div>
              <div v-if="row.breaks.length" class="ca-hist-day__breaks">
                <span class="ca-hist-day__breaks-label">{{ $t('ca_hist_day_col_breaks') }}:</span>
                <span v-for="item in row.breaks" :key="item.key" class="ca-hist-day__break">{{ item.label }}</span>
              </div>
              <div class="ca-hist-day__totals">
                {{ $t('ca_hist_day_col_gross') }} {{ row.grossLabel }} · {{ $t('ca_hist_day_col_breaks') }} {{ row.breakLabel }}
              </div>
            </div>
          </li>
        </ol>
      </div>
    </aside>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import utilService from '../../../../common/services/utilService'
import { formatMinutes } from '../cleanerActivityUtils'
import { buildCalendarMonths, formatDayLabel, formatMonthLabel, weekdayShortNames } from './cleanerHistoryUtils'

const FLASH_MS = 1600

export default {
  props: {
    person: { type: Object, required: true },
    dateFrom: { type: String, required: true },
    dateTo: { type: String, required: true },
    gmt: { type: [Number, String], default: null },
    locale: { type: String, default: 'en' },
    hasPrev: { type: Boolean, default: false },
    hasNext: { type: Boolean, default: false },
  },
  data() {
    return {
      flashDate: null,
      flashTimer: null,
      legend: ['worked', 'late', 'missed', 'no-clock-out'],
    }
  },
  computed: {
    ...mapGetters(['isRTL']),
    months() {
      return buildCalendarMonths(this.dateFrom, this.dateTo)
    },
    weekdayNames() {
      return weekdayShortNames(this.locale)
    },
    dayByDate() {
      return this.person.days.reduce((acc, day) => {
        acc[day.date] = day
        return acc
      }, {})
    },
    stats() {
      const t = this.person.totals
      return [
        { key: 'total', label: 'ca_hist_col_total', value: formatMinutes(t.grossMinutes) },
        { key: 'net', label: 'ca_hist_col_net', value: formatMinutes(t.netMinutes) },
        { key: 'breaks', label: 'ca_hist_col_breaks', value: formatMinutes(t.breakMinutes) },
      ]
    },
    dayRows() {
      return this.person.days.map((day) => ({
        date: day.date,
        label: formatDayLabel(day.date, this.locale, { withYear: true }),
        isWorked: day.isWorked,
        isMissed: day.isMissed,
        late: day.late,
        lateLabel: day.late ? formatMinutes(day.late.minutes) : '',
        lateTitle: day.late
          ? this.$t('ca_hist_late_tip', {
            time: day.sessions[0].clockInHm || this.formatHM(day.sessions[0].clockIn),
            shift: day.late.shiftName || '',
            start: day.late.shiftStartHm || this.formatHM(day.late.shiftStart),
          })
          : '',
        hasMissingClockOut: day.hasMissingClockOut,
        hasInProgress: day.hasInProgress,
        netLabel: formatMinutes(day.netMinutes),
        grossLabel: formatMinutes(day.grossMinutes),
        breakLabel: formatMinutes(day.breakMinutes),
        sessions: day.sessions.map((session, index) => ({
          key: `${index}-${session.clockIn}`,
          status: session.status,
          isAuto: session.isAuto,
          inLabel: session.clockInHm || this.formatHM(session.clockIn),
          outLabel: session.clockOut ? (session.clockOutHm || this.formatHM(session.clockOut)) : '…',
          nextDay: session.clockOutDate
            ? session.clockOutDate !== day.date
            : !!session.clockOut && utilService.companyLocalDateStr(session.clockOut, this.gmtValue) !== day.date,
        })),
        breaks: day.breaks.map((item, index) => ({
          key: `${index}-${item.start}`,
          label: item.isOpen
            ? `${item.startHm || this.formatHM(item.start)} → ${this.$t('ca_hist_open_break')}`
            : `${item.startHm || this.formatHM(item.start)} → ${item.endHm || this.formatHM(item.end)}`,
        })),
      }))
    },
    gmtValue() {
      return this.gmt != null && this.gmt !== '' ? this.gmt : 0
    },
  },
  watch: {
    'person.key'() {
      if (this.$refs.body) this.$refs.body.scrollTop = 0
    },
  },
  mounted() {
    this.onKeydown = (event) => {
      if (event.key === 'Escape') this.$emit('close')
    }
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown)
    if (this.flashTimer) clearTimeout(this.flashTimer)
  },
  methods: {
    formatHM(iso) {
      return utilService.formatHMInCompanyTz(iso, this.gmtValue)
    },
    monthLabel(month) {
      return formatMonthLabel(month.year, month.month, this.locale)
    },
    dayState(date) {
      if (date < this.dateFrom || date > this.dateTo) return 'outside'
      const day = this.dayByDate[date]
      if (!day) return 'none'
      if (day.isMissed) return 'missed'
      if (day.hasMissingClockOut) return 'no-clock-out'
      if (day.late) return 'late'
      return 'worked'
    },
    dayTitle(date) {
      const day = this.dayByDate[date]
      const label = formatDayLabel(date, this.locale)
      if (!day) return label
      if (day.isMissed) return `${label} · ${this.$t('ca_hist_missed')}`
      return `${label} · ${formatMinutes(day.netMinutes)}`
    },
    scrollToDay(date) {
      if (!date || !this.dayByDate[date]) return
      const element = this.$el.querySelector(`#ca-hist-day-${date}`)
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      this.flashDate = date
      if (this.flashTimer) clearTimeout(this.flashTimer)
      this.flashTimer = setTimeout(() => { this.flashDate = null }, FLASH_MS)
    },
  },
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

$worked: #5f9b46;
$warning: #e0a030;

.ca-hist-detail {
  position: fixed;
  inset: 0;
  // Same layer as the app's modals: filter-fixes.scss pins every select trigger at 9998,
  // so anything lower lets the toolbar's Working-area select paint over the panel.
  z-index: 9999;

  &__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(20, 30, 40, 0.28);
  }

  &__panel {
    position: absolute;
    top: 0;
    bottom: 0;
    inset-inline-end: 0;
    display: flex;
    flex-direction: column;
    width: min(560px, 100vw);
    background: #fff;
    box-shadow: -8px 0 24px rgba(0, 0, 0, 0.12);
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 18px 12px;
    border-bottom: 1px solid #eef0f3;
  }

  &__name {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 700;
    color: #2c3e50;
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
    font-size: 0.8rem;
    color: #7f8c8d;
  }

  &__badge {
    padding: 1px 7px;
    border-radius: 999px;
    background: #f1f3f6;
    color: #6b7785;
    font-size: 0.7rem;
    font-weight: 600;

    &.is-live {
      background: rgba(47, 143, 78, 0.12);
      color: $ca-clock-in;
    }
  }

  &__nav {
    display: flex;
    gap: 6px;

    button {
      width: 32px;
      height: 32px;
      border: 1px solid #e1e8ed;
      border-radius: 6px;
      background: #fff;
      color: #2c3e50;
      font-size: 1rem;
      line-height: 1;
      cursor: pointer;

      &:hover:not(:disabled) {
        background: #f7f9fb;
      }

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
    }
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    padding: 14px 18px 24px;
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-bottom: 16px;
  }

  &__stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px 10px;
    border: 1px solid #eef0f3;
    border-radius: 8px;
    background: #fbfcfd;

    &.is-late &-value {
      color: $ca-timeline-late;
    }

    &.is-missed &-value {
      color: $ca-breach;
    }
  }

  &__stat-value {
    font-size: 1.1rem;
    font-weight: 700;
    color: #2c3e50;
  }

  &__stat-label {
    font-size: 0.68rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #9aa5b1;
  }

  &__calendars {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 8px;
  }

  &__legend {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
    font-size: 0.72rem;
    font-weight: 600;
    color: #5a6573;
  }

  &__legend-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  &__empty {
    margin: 16px 0;
    color: #9aa5b1;
    text-align: center;
  }
}

.ca-hist-cal {
  flex: 1 1 230px;
  max-width: 260px;

  &__title {
    margin-bottom: 6px;
    font-size: 0.8rem;
    font-weight: 700;
    color: #2c3e50;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 3px;
  }

  &__weekday {
    font-size: 0.66rem;
    font-weight: 700;
    color: #9aa5b1;
    text-align: center;
  }

  &__day {
    aspect-ratio: 1;
    padding: 0;
    border: 1px solid transparent;
    border-radius: 5px;
    background: #f4f6f8;
    color: #5a6573;
    font-size: 0.72rem;
    font-weight: 600;
    cursor: default;

    &:not(:disabled) {
      cursor: pointer;

      &:hover {
        filter: brightness(0.95);
      }
    }

    &.is-pad {
      background: transparent;
    }

    &.is-outside {
      background: transparent;
      color: #cfd6de;
    }

    &.is-worked {
      background: $worked;
      color: #fff;
    }

    &.is-late {
      background: $worked;
      color: #fff;
      box-shadow: inset 0 -3px 0 $ca-timeline-late;
    }

    &.is-no-clock-out {
      background: #fff4e5;
      border-color: $warning;
      color: #9a5b0a;
    }

    &.is-missed {
      background: #fdecea;
      border-color: #f5c6c2;
      color: #922b21;
    }
  }

  &__swatch {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    border: 1px solid transparent;

    &.is-worked {
      background: $worked;
    }

    &.is-late {
      background: $worked;
      box-shadow: inset 0 -3px 0 $ca-timeline-late;
    }

    &.is-no-clock-out {
      background: #fff4e5;
      border-color: $warning;
    }

    &.is-missed {
      background: #fdecea;
      border-color: #f5c6c2;
    }
  }
}

.ca-hist-days {
  margin: 0;
  padding: 0;
  list-style: none;
}

.ca-hist-day {
  padding: 10px 12px;
  margin-bottom: 8px;
  border: 1px solid #eef0f3;
  border-radius: 8px;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  &.is-missed {
    background: #fffafa;
  }

  &.is-flash {
    border-color: #5f9b46;
    box-shadow: 0 0 0 3px rgba(95, 155, 70, 0.18);
  }

  &__head {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__date {
    font-size: 0.86rem;
    font-weight: 700;
    color: #2c3e50;
  }

  &__chips {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  &__chip {
    padding: 1px 7px;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 700;

    &.is-late {
      background: rgba(230, 126, 34, 0.12);
      color: #a85a12;
    }

    &.is-missed {
      background: #fdecea;
      color: #922b21;
    }

    &.is-warning {
      background: #fff4e5;
      color: #9a5b0a;
    }

    &.is-live {
      background: rgba(47, 143, 78, 0.12);
      color: $ca-clock-in;
    }
  }

  &__net {
    margin-inline-start: auto;
    font-size: 0.95rem;
    font-weight: 700;
    color: #2c3e50;
  }

  &__body {
    margin-top: 6px;
    font-size: 0.8rem;
    color: #5a6573;
  }

  &__punches,
  &__breaks {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 12px;
  }

  &__breaks {
    margin-top: 4px;
  }

  &__breaks-label {
    font-weight: 600;
    color: #7f8c8d;
  }

  &__punch {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    font-weight: 600;
    color: #2c3e50;

    &.is-missing_clock_out {
      color: #9a5b0a;
    }

    &.is-in_progress {
      color: $ca-clock-in;
    }
  }

  &__tag {
    padding: 0 5px;
    border-radius: 4px;
    background: #eef3f8;
    color: #3d5a73;
    font-size: 0.66rem;
    font-weight: 700;
    text-transform: uppercase;
  }

  &__totals {
    margin-top: 4px;
    font-size: 0.74rem;
    color: #9aa5b1;
  }
}

@media (max-width: 520px) {
  .ca-hist-detail__stats {
    grid-template-columns: 1fr;
  }
}
</style>
